import React, { useState } from 'react';
import { PlusIcon, TrashIcon, ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline';
import { uploadAPI } from '../../services/api';
import { handleApiError, getMediaTypeFromUrl, getImageUrl, showToast } from '../../utils/helpers';
import { useLanguage } from '../../contexts/LanguageContext';

const CarouselItemsManager = ({ items = [], onChange, disabled = false }) => {
  const { isRTL } = useLanguage();
  const [uploadingIndex, setUploadingIndex] = useState(null);

  const handleAddItem = () => {
    const newItem = { url: '', type: 'image', order: items.length + 1 };
    onChange([...items, newItem]);
  };

  const handleRemoveItem = (index) => {
    const newItems = items.filter((_, i) => i !== index).map((item, i) => ({
      ...item,
      order: i + 1
    }));
    onChange(newItems);
  };

  const handleMoveItem = (index, direction) => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === items.length - 1)) {
      return;
    }

    const newItems = [...items];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
    
    // Update order
    newItems.forEach((item, i) => {
      item.order = i + 1;
    });
    
    onChange(newItems);
  };

  const handleFileUpload = async (index, file) => {
    try {
      setUploadingIndex(index);
      
      const response = await uploadAPI.uploadImage(file);
      const fileUrl = response.data.url;
      const fileType = response.data.type || (file.type.startsWith('video/') ? 'video' : 'image');

      const newItems = [...items];
      newItems[index] = {
        ...newItems[index],
        url: fileUrl,
        type: fileType
      };
      
      onChange(newItems);
      showToast.success(isRTL ? 'تم رفع الملف بنجاح' : 'File uploaded successfully');
    } catch (error) {
      showToast.error(handleApiError(error));
    } finally {
      setUploadingIndex(null);
    }
  };

  const handleUrlChange = (index, url) => {
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      url: url,
      type: url ? (getMediaTypeFromUrl(url) === 'video' ? 'video' : 'image') : 'image'
    };
    onChange(newItems);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          {isRTL ? 'عناصر الكاروسيل' : 'Carousel Items'}
        </label>
        <button
          type="button"
          onClick={handleAddItem}
          disabled={disabled || uploadingIndex !== null}
          className="btn btn-secondary text-sm flex items-center gap-1"
        >
          <PlusIcon className="h-4 w-4" />
          {isRTL ? 'إضافة عنصر' : 'Add Item'}
        </button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
          <p className="text-gray-500 text-sm">
            {isRTL ? 'لا توجد عناصر في الكاروسيل' : 'No carousel items yet'}
          </p>
          <button
            type="button"
            onClick={handleAddItem}
            disabled={disabled}
            className="mt-2 btn btn-primary text-sm"
          >
            {isRTL ? 'إضافة أول عنصر' : 'Add First Item'}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item, index) => (
            <div
              key={index}
              className="border border-gray-300 rounded-lg p-4 bg-gray-50"
            >
              <div className="flex items-start gap-4">
                {/* Order Controls */}
                <div className={`flex flex-col gap-1 ${isRTL ? 'order-last' : ''}`}>
                  <button
                    type="button"
                    onClick={() => handleMoveItem(index, 'up')}
                    disabled={disabled || index === 0 || uploadingIndex !== null}
                    className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={isRTL ? 'نقل لأعلى' : 'Move up'}
                  >
                    <ArrowUpIcon className="h-4 w-4 text-gray-600" />
                  </button>
                  <span className="text-xs text-center text-gray-500 font-medium px-1">
                    {item.order || index + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleMoveItem(index, 'down')}
                    disabled={disabled || index === items.length - 1 || uploadingIndex !== null}
                    className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={isRTL ? 'نقل لأسفل' : 'Move down'}
                  >
                    <ArrowDownIcon className="h-4 w-4 text-gray-600" />
                  </button>
                </div>

                {/* Preview */}
                <div className="flex-shrink-0 w-24 h-24 bg-gray-200 rounded-lg overflow-hidden">
                  {item.url ? (
                    item.type === 'video' ? (
                      <video
                        src={getImageUrl(item.url)}
                        className="w-full h-full object-cover"
                        muted
                        playsInline
                        preload="metadata"
                        onError={(e) => {
                          const parent = e.target.parentElement;
                          parent.innerHTML = '';
                          const errorDiv = document.createElement('div');
                          errorDiv.className = 'w-full h-full flex items-center justify-center text-gray-400 text-xs';
                          errorDiv.textContent = isRTL ? 'فيديو' : 'Video';
                          parent.appendChild(errorDiv);
                        }}
                      />
                    ) : (
                      <img
                        src={getImageUrl(item.url)}
                        alt={`Carousel item ${index + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="14" x="50" y="50" text-anchor="middle" dominant-baseline="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                        }}
                      />
                    )
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                      {isRTL ? 'معاينة' : 'Preview'}
                    </div>
                  )}
                </div>

                {/* Inputs */}
                <div className="flex-1 space-y-2">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      {isRTL ? 'رابط الملف أو رفع ملف' : 'File URL or Upload'}
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={item.url || ''}
                        onChange={(e) => handleUrlChange(index, e.target.value)}
                        placeholder={isRTL ? 'أدخل رابط الملف' : 'Enter file URL'}
                        disabled={disabled || uploadingIndex !== null}
                        className="flex-1 input-field"
                      />
                      <label className="btn btn-secondary text-sm cursor-pointer whitespace-nowrap">
                        {uploadingIndex === index ? (
                          <span>{isRTL ? 'جاري الرفع...' : 'Uploading...'}</span>
                        ) : (
                          <>
                            📤 {isRTL ? 'رفع' : 'Upload'}
                            <input
                              type="file"
                              accept="image/*,video/mp4,video/mpeg,video/quicktime,video/x-msvideo"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) handleFileUpload(index, file);
                                e.target.value = '';
                              }}
                              className="hidden"
                              disabled={disabled || uploadingIndex !== null}
                            />
                          </>
                        )}
                      </label>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-600">
                      {isRTL ? 'النوع:' : 'Type:'}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded ${
                      item.type === 'video' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {item.type === 'video' ? '🎬 Video' : '🖼️ Image'}
                    </span>
                  </div>
                </div>

                {/* Remove Button */}
                <button
                  type="button"
                  onClick={() => handleRemoveItem(index)}
                  disabled={disabled || uploadingIndex !== null}
                  className="p-2 text-red-600 hover:bg-red-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  title={isRTL ? 'حذف' : 'Remove'}
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {items.length > 0 && (
        <p className="text-xs text-gray-500">
          {isRTL 
            ? `إجمالي ${items.length} ${items.length === 1 ? 'عنصر' : 'عناصر'}`
            : `Total: ${items.length} ${items.length === 1 ? 'item' : 'items'}`
          }
        </p>
      )}
    </div>
  );
};

export default CarouselItemsManager;


